/**
 * Members Page
 * Displays ALL users in the Appwrite project.
 * Allows creating new users, blocking/unblocking, and deleting users.
 * Roles are managed per-team on the Team Detail page, NOT here.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { listAllUsers, createUser, updateUserStatus, deleteUser } from '../../appwrite';
import { ID } from 'appwrite';
import './Members.css';

const Members = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalUsers, setTotalUsers] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // User ID of action in progress

  // Create user modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // User ID pending deletion

  /**
   * Fetches all project users.
   */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listAllUsers({ limit: 100, search: searchQuery || undefined });
      setUsers(response.users);
      setTotalUsers(response.total);
    } catch (err) {
      setError(err.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /**
   * Creates a new user from the modal form.
   */
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    setError('');
    try {
      await createUser(ID.unique(), newUserEmail, newUserPassword, newUserName);
      setShowCreateModal(false);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      showSuccess('User created successfully.');
      await fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to create user.');
    } finally {
      setCreatingUser(false);
    }
  };

  /**
   * Toggles a user's blocked status.
   */
  const handleToggleBlock = async (user) => {
    setActionLoading(user.$id);
    try {
      const newStatus = !user.status; // true = active, false = blocked
      await updateUserStatus(user.$id, newStatus);
      showSuccess(`${user.name || user.email} has been ${newStatus ? 'unblocked' : 'blocked'}.`);
      await fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to update user status.');
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Deletes a user after confirmation.
   */
  const handleDeleteUser = async (userId) => {
    setActionLoading(userId);
    setDeleteConfirm(null);
    try {
      await deleteUser(userId);
      showSuccess('User deleted successfully.');
      await fetchUsers();
    } catch (err) {
      setError(err.message || 'Failed to delete user.');
    } finally {
      setActionLoading(null);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Loading state
  if (loading) {
    return (
      <div className="members-page">
        <div className="members-loading">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="members-page">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Members</h1>
          <span className="page-count">{totalUsers} total</span>
        </div>
        <div className="page-header-right">
          {/* Search */}
          <div className="members-search">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="8" cy="8" r="5.5" />
              <path d="M12.5 12.5L16 16" />
            </svg>
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {/* Create user button */}
          <button className="create-btn" onClick={() => setShowCreateModal(true)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3V13M3 8H13" />
            </svg>
            New User
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="members-alert members-alert-error">
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMessage && (
        <div className="members-alert members-alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      {/* Users table */}
      <div className="members-table-container">
        <table className="members-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Registered</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.$id} className={actionLoading === user.$id ? 'saving' : ''}>
                {/* User info */}
                <td>
                  <div className="member-info">
                    <div className="member-avatar">
                      {(user.name || user.email)?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="member-details">
                      <span className="member-name">{user.name || 'Unnamed'}</span>
                      <span className="member-email">{user.email}</span>
                    </div>
                  </div>
                </td>

                {/* Registration date */}
                <td>
                  <span className="member-date">
                    {new Date(user.registration).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'short', day: 'numeric'
                    })}
                  </span>
                </td>

                {/* Status */}
                <td>
                  <span className={`status-badge ${user.status ? 'status-active' : 'status-blocked'}`}>
                    {user.status ? 'Active' : 'Blocked'}
                  </span>
                </td>

                {/* Actions */}
                <td>
                  <div className="member-actions">
                    {/* Block / Unblock */}
                    <button
                      className={`action-btn ${user.status ? 'action-block' : 'action-unblock'}`}
                      onClick={() => handleToggleBlock(user)}
                      disabled={actionLoading === user.$id}
                      title={user.status ? 'Block user' : 'Unblock user'}
                    >
                      {user.status ? 'Block' : 'Unblock'}
                    </button>
                    {/* Delete */}
                    {deleteConfirm === user.$id ? (
                      <>
                        <button
                          className="action-btn action-delete-confirm"
                          onClick={() => handleDeleteUser(user.$id)}
                          disabled={actionLoading === user.$id}
                        >
                          Confirm
                        </button>
                        <button
                          className="action-btn action-cancel"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="action-btn action-delete"
                        onClick={() => setDeleteConfirm(user.$id)}
                        disabled={actionLoading === user.$id}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Empty state */}
        {users.length === 0 && !loading && (
          <div className="members-empty">
            <p>No users found.</p>
          </div>
        )}
      </div>

      {/* ===== Create User Modal ===== */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Create New User</h2>
            <form className="modal-form" onSubmit={handleCreateUser}>
              <div className="modal-field">
                <label>Name</label>
                <input
                  type="text"
                  placeholder="Full name"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  required
                />
              </div>
              <div className="modal-field">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  required
                />
              </div>
              <div className="modal-field">
                <label>Password</label>
                <input
                  type="password"
                  placeholder="Min 8 characters"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="action-btn action-cancel" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="action-btn action-save" disabled={creatingUser}>
                  {creatingUser ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Members;
